import { showToastMessage } from 'enable-ui';

import { LessonType, StudyContentType } from 'modules/calendar/core/enum';
import { EventSession } from 'modules/calendar/core/type';
import { eventTypesPillButton } from 'modules/calendar/pages';
import { Classes } from 'modules/classes/core/type';

import { FilterConfig, SelectedFilters } from 'components/filter-panel/type';

import { format, isAfter, isBefore } from 'date-fns';
import { includes, isEmpty, map, some, uniqBy } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

export const createFilterConfig = (events: EventSession[], classes: Classes[]): FilterConfig[] => {
  const uniqueUsers = uniqBy(
    events.flatMap((event) => event.event.users || []),
    'id',
  );

  const uniqueCenters = uniqBy(
    events.filter((event) => event.station).map((event) => event.station),
    'id',
  );

  return [
    {
      key: 'eventType',
      title: 'Event types',
      options: [
        {
          id: '1',
          label: eventTypesPillButton('Class lesson', COLOR.purpel['purpel-6']),
        },
        {
          id: '2',
          label: eventTypesPillButton('Challenge', COLOR_CONFIG.cyan),
        },
        {
          id: '3',
          label: eventTypesPillButton('Other events', COLOR_CONFIG.magenta),
        },
      ],
    },
    {
      key: 'classes',
      title: 'Classes',
      options: [
        {
          id: 'selectClasses',
          label: 'Select classes',
          type: 'selectable',
          options: map(classes, (classItem) => ({
            label: classItem.name,
            value: classItem.id,
            subLabel: classItem.code,
          })),
          loadMoreKey: 'classes',
          placeholder: 'Search with ID or class name',
        },
      ],
    },
    // {
    //   key: 'classTypes',
    //   title: 'Class types',
    //   options: [
    //     { id: 'single', label: 'Single session' },
    //     { id: 'multiple', label: 'Multiple sessions' },
    //   ],
    // },
    {
      key: 'lessonType',
      title: 'Lesson types',
      options: [
        { id: String(LessonType.DIY), label: 'DIY lesson' },
        { id: String(LessonType.Supervised), label: 'Supervised lesson' },
        { id: 'assessment', label: 'Assessment' },
      ],
    },

    {
      key: 'users',
      title: 'Users',
      options: [
        {
          id: 'selectUsers',
          label: 'Select users',
          type: 'selectable',
          options: uniqueUsers.map((user) => ({
            label: user.name,
            value: user.id,
            hasAvatar: true,
            src: user?.avatar,
          })),
          placeholder: 'Search with name or role',
          localSearch: true,
        },
      ],
    },
    {
      key: 'centers',
      title: 'Centers',
      options: [
        {
          id: 'selectCenters',
          label: 'Select centers',
          type: 'selectable',
          options: uniqueCenters.map((center) => ({
            label: center.name,
            value: center.id,
            subLabel: `ID ${center.stationId && center.stationId + ','} ${center.address && `${center.address}`}
            ${center.city ? `, ${center.city}` : ''}
            ${center.postalCode ? `, ${center.postalCode}` : ''}
`,
          })) as any,
          placeholder: 'Search with ID, center name or address',
          localSearch: true,
        },
      ],
    },
  ];
};

export const filterEvents = (events: EventSession[], filters: SelectedFilters) => {
  if (isEmpty(filters)) return events;

  return events.filter((event) => {
    const { event: eventData, station, eventContent } = event;

    return (
      (!filters.eventType?.length || includes(filters?.eventType, String(eventData.type))) &&
      (!filters.classes?.length || includes(filters.classes, eventData.relationId)) &&
      (!filters.lessonType?.length ||
        (eventContent?.type === StudyContentType.Assesment &&
          includes(filters.lessonType, 'assessment')) ||
        (eventContent?.type === StudyContentType.Lesson &&
          eventContent?.chore?.type &&
          includes(filters?.lessonType, String(eventContent?.chore?.type)))) &&
      (!filters.users?.length ||
        some(eventData?.users, (user) => includes(filters?.users, user?.id))) &&
      (!filters.centers?.length || (station && includes(filters?.centers, station?.id)))
    );
  });
};

export const validateDateRange = (startDate: Date | null, endDate: Date | null): string => {
  if (startDate && endDate && isAfter(startDate, endDate)) {
    return 'Start date cannot be after end date';
  }

  if (startDate && endDate && isBefore(endDate, startDate)) {
    return 'End date cannot be before start date';
  }

  return '';
};

/**
 * Downloads the QR code from the given React.RefObject<HTMLDivElement> as a PNG image.
 *
 * @param qrCode - The React.RefObject<HTMLDivElement> containing the QR code.
 * @returns A function that initiates the download process.
 */
export const downloadQRCode = (qrCode: React.RefObject<HTMLDivElement>) => () => {
  try {
    if (!qrCode.current) throw new Error('QR code is not ready');

    const canvas = qrCode.current.querySelector('canvas') as HTMLCanvasElement;

    if (!canvas) throw new Error('Canvas element not found');

    canvas.toBlob((blob) => {
      if (!blob) throw new Error('Failed to convert canvas to Blob');

      const a = document.createElement('a');

      a.href = URL.createObjectURL(blob);
      a.download = 'qr-code.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }, 'image/png');
  } catch (error) {
    console.error('Download failed:', error);
    showToastMessage('error', 'Failed to download QR code. Please try again.');
  }
};

export const getWeekDays = (selectedDates: Date[]) =>
  selectedDates.map((date) => format(date, 'EEE'));
